// BigQuestionHistoryPage.jsx (card layout)
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import "./baseStyles.css";

const apiUrl = process.env.REACT_APP_API_URL;
const HISTORY_OPERATION = "history_big_question";
const TZ = "America/Chicago";

const BigQuestionHistoryPage = () => {
  const navigate = useNavigate();

  const [userId, setUserId] = useState("");
  const [loginUserId, setLoginUserId] = useState("");

  const [bigQuestionCampaigns, setBigQuestionCampaigns] = useState([]);
  const [campaignName, setCampaignName] = useState("");

  const [rows, setRows] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [loadingRows, setLoadingRows] = useState(false);

  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");

  const idToken = useMemo(() => localStorage.getItem("idToken"), []);

  // ---------- utilities ----------
  const clearStatus = () => {
    setError("");
    setInfoMessage("");
  };

  const authGuard = () => {
    if (!idToken) {
      setError("User is not authenticated. Please log in.");
      navigate("/login");
      return null;
    }
    try {
      const decoded = jwtDecode(idToken);
      if (decoded.exp * 1000 < Date.now()) {
        localStorage.removeItem("idToken");
        setError("Session expired. Please log in again.");
        navigate("/login");
        return null;
      }
      return decoded;
    } catch (e) {
      console.error("❌ Token decode failed", e);
      localStorage.removeItem("idToken");
      setError("Authentication error. Please log in again.");
      navigate("/login");
      return null;
    }
  };

  const displayCampaignName = (raw) => {
    if (!raw) return "";
    let d = raw;
    if (userId && d.startsWith(`${userId}_`)) d = d.replace(`${userId}_`, "");
    if (d.toLowerCase().startsWith("bigquestion_")) d = d.replace(/^bigquestion_/i, "");
    return d;
  };

  // winner check: match aiPick to userId/loginId/exact answer
  const isWinner = (rowPick, partnerObj) => {
    if (!rowPick) return false;
    const pick = String(rowPick).trim();
    return (
      pick === (partnerObj.partnerUserId || "") ||
      pick === (partnerObj.partnerLoginId || "") ||
      pick === (partnerObj.Answer1 || "")
    );
  };

  // short date from lastAccessedReadable or epoch
  const shortDate = (row) => {
    const fmt = (d) =>
      d.toLocaleDateString(undefined, {
        timeZone: TZ,
        month: "numeric",
        day: "numeric",
        year: "2-digit",
      });

    if (row.lastAccessedReadable) {
      const isoish = row.lastAccessedReadable.replace(" ", "T") + "Z";
      const d = new Date(isoish);
      if (!isNaN(d)) return fmt(d);
    }
    if (row.lastAccessed != null && row.lastAccessed !== "") {
      const n = Number(row.lastAccessed);
      if (!Number.isNaN(n)) {
        const ms = n > 1e12 ? n : n * 1000;
        const d = new Date(ms);
        if (!isNaN(d)) return fmt(d);
      }
    }
    return "";
  };

  // ---------- initial auth + load campaigns ----------
  useEffect(() => {
    clearStatus();
    const decoded = authGuard();
    if (!decoded) return;

    setUserId(decoded.sub);
    setLoginUserId(decoded["cognito:username"]);
    void fetchCampaigns(decoded.sub);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCampaigns = async (uid) => {
    setLoadingCampaigns(true);
    try {
      const res = await axios.post(
        `${apiUrl}`,
        { operation: "fetch_all", userid: uid },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      const data = Array.isArray(res.data) ? res.data : [];
      const filtered = data.filter((c) => c.campaignListCampaignType === "BigQuestion");
      setBigQuestionCampaigns(filtered);

      if (filtered.length === 1) {
        const only = filtered[0].campaignName;
        setCampaignName(only);
        await fetchHistory(uid, only);
      }
    } catch (e) {
      console.error("❌ fetch_all failed", e);
      if (e?.response?.data?.message === "The incoming token has expired") {
        localStorage.removeItem("idToken");
        setError("Session expired. Please log in again.");
        navigate("/login");
        return;
      }
      setError("Failed to load campaigns.");
    } finally {
      setLoadingCampaigns(false);
    }
  };

  // ---------- load history for selected campaign ----------
  const fetchHistory = async (uid, cName) => {
    if (!uid || !cName) return;
    clearStatus();
    setLoadingRows(true);
    setRows([]);

    try {
      const res = await axios.post(
        `${apiUrl}`,
        {
          operation: HISTORY_OPERATION,
          userid: uid,
          campaignName: cName,
        },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      const items = Array.isArray(res.data) ? res.data : [];

      const normalized = items.map((it) => {
        const partners = Array.isArray(it.partnerResponses) ? it.partnerResponses.slice() : [];

        // Ensure viewer shows up as a participant if backend didn’t include them
        const selfAnswer = (it.Answer1 ?? "").trim();

        const alreadyHasSelf =
          partners.some((p) => p.partnerUserId === userId) ||
          partners.some((p) => p.partnerLoginId === loginUserId) ||
          (
            selfAnswer &&
            partners.some((p) => (p.Answer1 ?? "").trim() === selfAnswer)
          );

        if (!alreadyHasSelf) {
          partners.push({
            partnerUserId: userId,
            partnerLoginId: loginUserId,
            partnerFirstName: loginUserId,
            Answer1: it.Answer1 ?? "",
            bigQuestionPoints: it.bigQuestionPoints ?? 0,
            bigQuestionPlacement: it.bigQuestionPlacement ?? 0,
            bigQuestionPlayed: it.bigQuestionPlayed ?? (selfAnswer ? 1 : 0),
            bigQuestionScoringVersion: it.bigQuestionScoringVersion ?? "",
          });
        }

        return {
          sequenceNumber: it.sequenceNumber ?? it.seq ?? 0,
          campaignItemsId: it.campaignItemsId ?? it.itemId ?? "",
          campaignItemsText: it.campaignItemsText ?? it.questionText ?? "",
          aiSummary: it.aiSummary ?? it?.aiReview?.summary ?? "",
          aiPick: it.aiPick ?? it.aiWinner ?? "",
          bigQuestionPoints: it.bigQuestionPoints ?? 0,
          bigQuestionPlacement: it.bigQuestionPlacement ?? 0,
          bigQuestionPlayed: it.bigQuestionPlayed ?? ((it.Answer1 ?? "").trim() ? 1 : 0),
          bigQuestionScoringVersion: it.bigQuestionScoringVersion ?? "",
          aiScoringVersion: it.aiScoringVersion ?? "",
          aiRankedLoginIds: Array.isArray(it.aiRankedLoginIds) ? it.aiRankedLoginIds : [],
          lastAccessed: it.lastAccessed ?? it.dataAccessed ?? null,
          lastAccessedReadable: it.lastAccessedReadable ?? it.dataAccessedReadable ?? "",
          partnerResponses: partners.map((p) => ({
            partnerUserId: p.partnerUserId ?? "",
            partnerLoginId: p.partnerLoginId ?? "",
            partnerFirstName: p.partnerFirstName ?? p.partnerLoginId ?? "Partner",
            Answer1: p.Answer1 ?? "",
            bigQuestionPoints: p.bigQuestionPoints ?? 0,
            bigQuestionPlacement: p.bigQuestionPlacement ?? 0,
            bigQuestionPlayed: p.bigQuestionPlayed ?? ((p.Answer1 ?? "").trim() ? 1 : 0),
            bigQuestionScoringVersion: p.bigQuestionScoringVersion ?? "",
          })),
        };
      });

      setRows(
        normalized
          .slice()
          .sort((a, b) => (b.sequenceNumber ?? 0) - (a.sequenceNumber ?? 0))
      );
    } catch (e) {
      console.error("❌ history fetch failed", e);
      if (e?.response?.data?.message === "The incoming token has expired") {
        localStorage.removeItem("idToken");
        setError("Session expired. Please log in again.");
        navigate("/login");
        return;
      }
      setError("Failed to load history.");
    } finally {
      setLoadingRows(false);
    }
  };

  // ---------- handlers ----------
  const onChangeCampaign = async (val) => {
    setCampaignName(val);
    await fetchHistory(userId, val);
  };

  // ---------- UI ----------
  return (
    <div className="container">
      <div className="header-with-info">
        <h5 className="section-header">Big Question — History</h5>
      </div>

      {error && <p className="error-message">{error}</p>}
      {infoMessage && <p className="success-message">{infoMessage}</p>}

      {/* Campaign selector */}
      {loadingCampaigns ? (
        <div>Loading campaigns…</div>
      ) : bigQuestionCampaigns.length ? (
        <div className="form-group dropdown-container" style={{ marginTop: 8 }}>
          <select
            className="compact-select"
            value={campaignName}
            onChange={(e) => onChangeCampaign(e.target.value)}
          >
            <option value="">-- Select Campaign --</option>
            {bigQuestionCampaigns.map((c) => (
              <option key={c.campaignName} value={c.campaignName}>
                {displayCampaignName(c.campaignName)}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <p>No Big Question campaigns found.</p>
      )}

      {/* Cards */}
      <div style={{ marginTop: 12 }}>
        {loadingRows ? (
          <div>Loading history…</div>
        ) : !campaignName ? (
          <p>Select a campaign to view history.</p>
        ) : rows.length === 0 ? (
          <p>No history yet for this campaign.</p>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            {rows.map((r) => (
              <div className="card" key={r.campaignItemsId || `${r.sequenceNumber}`}>
                {/* Card header: Seq + short date */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <h4 style={{ margin: 0 }}>
                    #{r.sequenceNumber}
                    {shortDate(r) ? <span style={{ fontSize: "0.85rem", color: "#666" }}> • {shortDate(r)}</span> : null}
                  </h4>
                </div>

                {/* Question */}
                <div style={{ marginTop: 6, fontWeight: 600 }}>{r.campaignItemsText}</div>

                {/* AI Summary */}
                {r.aiSummary ? (
                  <div style={{ marginTop: 8, fontSize: "0.95rem" }}>
                    <span style={{ fontWeight: 600 }}>AI Summary:</span> {r.aiSummary}
                  </div>
                ) : null}

                {/* Partners as sub-cards */}
                <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                  {r.partnerResponses.map((p, idx) => {
                    const placement = Number(p.bigQuestionPlacement || 0);

                    // Backward compatibility:
                    // - New scored rows use explicit placement.
                    // - Old rows with no placement still use aiPick.
                    const historicalWinner =
                      placement === 0 && isWinner(r.aiPick, p);

                    const winner =
                      placement === 1 || historicalWinner;

                    const placementBadge =
                      placement === 1
                        ? "🥇 1st · 5 pts"
                        : placement === 2
                        ? "🥈 2nd · 3 pts"
                        : placement === 3
                        ? "🥉 3rd · 1 pt"
                        : historicalWinner
                        ? "🏆 AI Pick"
                        : "";

                    const isSelf =
                      p.partnerUserId === userId || p.partnerLoginId === loginUserId;

                    return (
                      <div
                        key={`${r.sequenceNumber}-p-${idx}`}
                        className="response-card"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 4,
                          border: winner ? "1px solid #f0c040" : undefined,
                          boxShadow: winner ? "0 0 0 2px rgba(240,192,64,0.15) inset" : undefined,
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div style={{ fontWeight: 600 }}>
                            {p.partnerFirstName || p.partnerLoginId || p.partnerUserId}
                            {isSelf ? " (you)" : ""}
                          </div>
                          {placementBadge ? (
                            <span
                              style={{
                                fontSize: "0.75rem",
                                padding: "2px 8px",
                                borderRadius: 999,
                                background:
                                  placement === 1 || historicalWinner
                                    ? "#fff3cd"
                                    : placement === 2
                                    ? "#f1f3f4"
                                    : "#f7eee5",
                                border:
                                  placement === 1 || historicalWinner
                                    ? "1px solid #ffe08a"
                                    : placement === 2
                                    ? "1px solid #d0d4d8"
                                    : "1px solid #dfc3a5",
                                whiteSpace: "nowrap",
                              }}
                              title={
                                placement
                                  ? `Big Question placement: ${placement}`
                                  : "Historical AI Pick"
                              }
                            >
                              {placementBadge}
                            </span>
                          ) : null}
                        </div>
                        <div style={{ whiteSpace: "pre-wrap" }}>
                          {p.Answer1 || <em style={{ color: "#888" }}>No answer</em>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <br />
      <button className="save-button" onClick={() => navigate("/list-manager")}>
        Back to Campaigns
      </button>
    </div>
  );
};

export default BigQuestionHistoryPage;
